import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchJson } from '../lib/api';

const TEXTS = {
  es: {
    title: 'Recuperar contraseña',
    subtitle: 'Ingresa tu email para recibir un link de recuperación',
    emailLabel: 'Email',
    emailPlaceholder: 'tu@email.com',
    sendButton: 'Enviar link de recuperación',
    backToLogin: 'Volver al inicio de sesión',
    sending: 'Enviando...',
    success: 'Email enviado',
    successMessage: 'Si la cuenta existe, recibirás un email con instrucciones para recuperar tu contraseña.',
    supportLink: 'Contactar soporte si no llega',
    supportHint: 'Si no recibes el email, puedes pedir ayuda directamente a soporte.',
    error: 'Error al enviar el email. Intenta de nuevo.',
    resendLink: 'Enviar de nuevo',
    formError: 'Por favor ingresa un email válido.',
  },
  pt: {
    title: 'Recuperar senha',
    subtitle: 'Digite seu email para receber um link de recuperação',
    emailLabel: 'Email',
    emailPlaceholder: 'seu@email.com',
    sendButton: 'Enviar link de recuperação',
    backToLogin: 'Voltar para login',
    sending: 'Enviando...',
    success: 'Email enviado',
    successMessage: 'Se a conta existir, você receberá um email com instruções para recuperar sua senha.',
    supportLink: 'Falar com suporte se não chegar',
    supportHint: 'Se você não receber o email, pode pedir ajuda diretamente ao suporte.',
    error: 'Erro ao enviar o email. Tente novamente.',
    resendLink: 'Enviar novamente',
    formError: 'Por favor, digite um email válido.',
  },
  en: {
    title: 'Recover password',
    subtitle: 'Enter your email to receive a recovery link',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    sendButton: 'Send recovery link',
    backToLogin: 'Back to login',
    sending: 'Sending...',
    success: 'Email sent',
    successMessage: 'If the account exists, you will receive an email with instructions to recover your password.',
    supportLink: 'Contact support if it does not arrive',
    supportHint: 'If you do not receive the email, you can contact support directly.',
    error: 'Error sending email. Try again.',
    resendLink: 'Send again',
    formError: 'Please enter a valid email.',
  },
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const lang = (navigator.language || 'es').split('-')[0];
  const t = TEXTS[lang] || TEXTS.es;

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.includes('@')) {
      setError(t.formError);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchJson('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(response.error || t.error);
      }
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setError(null);
  };

  return (
    <div className="page-shell">
      <section className="hero-panel hero-panel--home">
        <div className="hero-copy">
          <p className="eyebrow">Recuperación</p>
          <h1>{t.title}</h1>
          <p className="hero-text">{t.subtitle}</p>
        </div>
      </section>

      <section className="home-grid">
        <article className="panel auth-panel">
          {submitted ? (
            <div className="forgot-password-success">
              <div className="success-icon">✓</div>
              <h2>{t.success}</h2>
              <p>{t.successMessage}</p>
              <p className="forgot-password-fallback">{t.supportHint}</p>
              <div className="forgot-password-actions">
                <button onClick={handleReset} className="btn-secondary">
                  {t.resendLink}
                </button>
                <a
                  href={`${import.meta.env.BASE_URL}support.html?recuperar=1`}
                  className="btn-secondary"
                >
                  {t.supportLink}
                </a>
                <button onClick={() => navigate('/login')} className="btn-ghost">
                  {t.backToLogin}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <label>
                {t.emailLabel}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  required
                />
              </label>

              {error && <div className="alert error">{error}</div>}

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? t.sending : t.sendButton}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="btn-ghost"
                >
                  {t.backToLogin}
                </button>
              </div>
            </form>
          )}
        </article>

        <aside className="panel quick-panel">
          <p className="eyebrow">Información</p>
          <h2>¿Necesitas ayuda?</h2>
          <ul className="feature-list">
            <li>Si el email no llega, revisa la carpeta de spam.</li>
            <li>El link de recuperación es válido por 1 hora.</li>
            <li>Si continúas con problemas, contacta soporte.</li>
          </ul>
        </aside>
      </section>
    </div>
  );
}
